import React, {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Autoplay, Navigation, EffectFade} from 'swiper/modules'
import {HStack, Box, Flex, useMultiStyleConfig, StackProps, FlexProps} from '@chakra-ui/react'
import {Icon} from '_uiComponents'
import {debounce} from 'lodash'
import {useViewport} from '_customHooks'

interface HTMLElementWithSwiper extends HTMLElement {
    swiper?: {
        destroy: (deleteInstance: boolean, cleanStyles: boolean) => void
        init: () => void
    }
}

interface BannerHeaderProps {
    children: React.ReactNode
    isLoop?: boolean
    variant?: 'transparent' | 'solid'
}

/**
 * @name BannerHeader
 *
 * @component
 *
 * @description
 * Renders banner header slider.
 *
 * @param {BannerHeaderProps} props - The properties object.
 * @param {React.ReactNode} props.children - children to render
 * @param {boolean} [props.isLoop] - Boolean to indicate if the swiper is looping
 * @param {['transparent' | 'solid']} [props.variant] - Variant version of the banner
 *
 * @returns {JSX.Element} Rendered `BannerHeader` component.
 */
const BannerHeader: React.FC<BannerHeaderProps> = ({isLoop = true, variant = 'solid', children}) => {
    const styles = useMultiStyleConfig('BannerHeader', {variant}) as Record<string, StackProps | FlexProps>
    const {formatMessage} = useIntl()
    const {isMobile} = useViewport()
    const [isResizeSet, setIsResizeSet] = useState(false)

    const handleResize = useCallback(() => {
        const swiperElement = (document?.querySelector("[data-testid='banner-header'] .swiper") as HTMLElementWithSwiper)?.swiper

        if (swiperElement) {
            // `destroy(deleteInstance, cleanStyles)`: pass `deleteInstance = false`. With `true`, Swiper
            // nulls out the instance and its `params`, so the immediately following `init()` crashes in
            // `mount()` while reading `swiper.params.el` ("swiper.params is undefined" — visible in
            // Firefox on window resize). Keeping the instance lets `init()` re-run cleanly; `cleanStyles`
            // stays `true` so the DOM is reset before re-init.
            swiperElement.destroy(false, true)
            swiperElement.init()
        }
    }, [])

    const debouncedHandleResize = useCallback(debounce(handleResize, 200), [handleResize])

    useEffect(() => {
        if (isMobile && !isResizeSet) {
            window.addEventListener('resize', debouncedHandleResize)
            setIsResizeSet(true)
            return () => {
                window.removeEventListener('resize', debouncedHandleResize)
            }
        } else if (!isMobile) {
            setIsResizeSet(false)
        }
    }, [debouncedHandleResize, isMobile])

    return (
        <Box {...styles.wrapper} data-testid="banner-header">
            <HStack sx={styles.container}>
                <Flex
                    as="button"
                    {...styles.navigationPrev}
                    aria-label={formatMessage({id: 'accessibility.button.slide.previous', defaultMessage: 'Previous message'})}
                    className={`swiper-button-prev swiper-button-prev-banner-header`}
                    data-testid="swiper-button-prev-banner-header"
                >
                    <Icon name="IconChevronLeft" size="sm" />
                </Flex>
                <Swiper
                    modules={[Navigation, Autoplay, EffectFade]}
                    navigation={{
                        prevEl: `.swiper-button-prev-banner-header`,
                        nextEl: `.swiper-button-next-banner-header`,
                    }}
                    className={'swiper-banner-header'}
                    fadeEffect={{crossFade: true}}
                    effect={'fade'}
                    slidesPerView={1}
                    loop={isLoop}
                    updateOnWindowResize={true}
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                    }}
                >
                    {React.Children.map(children, (child, index) => (
                        <>
                            <SwiperSlide key={`banner-header-${index}`}>{child}</SwiperSlide>
                        </>
                    ))}
                </Swiper>
                <Flex
                    as="button"
                    {...styles.navigationNext}
                    aria-label={formatMessage({id: 'accessibility.button.slide.next', defaultMessage: 'Next message'})}
                    className={`swiper-button-next swiper-button-next-banner-header`}
                    data-testid="swiper-button-next-banner-header"
                >
                    <Icon name="IconChevronRight" size="sm" />
                </Flex>
            </HStack>
        </Box>
    )
}

export {BannerHeader}
